// ✅ src/pages/Sales/EMIPaymentCustomer.jsx
// FULL FILE - API INTEGRATED + CHECKBOX SELECT + LOCALSTORAGE HEADERS ADDED
//
// Backend mount:
//   app.use("/api/emiListing", emiPaymentListing);
//
// APIs used:
// 1) GET /api/emiListing/assetsubtypes
// 2) GET /api/emiListing/search?enquiryNumber=&customerName=&customerId=&assetSubType=&doB=&paymentStatus=
//
// Navigation:
// - View selected rows -> /Sales/EMIPaymentEntry (passes selectedRows in route state)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaEye
} from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const EMIPaymentCustomer = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId],
  );

  // Search form state
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    customerId: "",
    assetSubType: "",
    doB: "",
    paymentStatus: "",
  });

  // Dropdown options from API
  const [assetSubTypes, setAssetSubTypes] = useState([]);

  // Table data from API
  const [emiCustomerData, setEmiCustomerData] = useState([]);

  // Selected rows for view
  const [selectedRows, setSelectedRows] = useState([]);

  const paymentStatuses = useMemo(() => ["Completed", "Pending"], []);

  // -------------------------
  // Helpers
  // -------------------------
  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const getPaymentStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "completed":
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getDueRemainingColor = (remaining) => {
    const n = Number(remaining || 0);
    if (n <= 5) return "bg-red-100 text-red-800";
    if (n <= 15) return "bg-yellow-100 text-yellow-800";
    return "bg-green-100 text-green-800";
  };

  // ✅ Unique row key
  const rowKey = (r) => `${r.customerId || ""}__${r.assetCode || ""}`;

  const isRowSelected = (row) =>
    selectedRows.some((r) => rowKey(r) === rowKey(row));

  const handleRowSelect = (row, checked) => {
    if (checked) {
      setSelectedRows((prev) => {
        if (prev.some((r) => rowKey(r) === rowKey(row))) return prev;
        return [...prev, row];
      });
    } else {
      setSelectedRows((prev) => prev.filter((r) => rowKey(r) !== rowKey(row)));
    }
  };

  const getRowId = (row) =>
    Number(
      row.salesLeadId ||
        row.emiPaymentId ||
        row.fld_emi_paymentid ||
        row.id ||
        0,
    );

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows([...emiCustomerData]);
    } else {
      setSelectedRows([]);
    }
  };

  // -------------------------
  // API Calls
  // -------------------------
  const fetchAssetSubTypes = async () => {
    try {
      const { data } = await axios.get(
        `${BASE_URL}/api/emiListing/assetsubtypes`,
        {
          headers: commonHeaders,
        },
      );

      if (data?.success) {
        setAssetSubTypes(data.data || []);
      } else {
        toast.error(data?.message || "Failed to load asset sub types");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load asset sub types");
    }
  };

  const buildQueryParams = () => ({
    enquiryNumber: searchData.enquiryNumber || "",
    customerName: searchData.customerName || "",
    customerId: searchData.customerId || "",
    assetSubType: searchData.assetSubType || "",
    doB: searchData.doB || "",
    paymentStatus: searchData.paymentStatus || "",
  });

  const handleSearch = async () => {
    const hasSearchCriteria = Object.values(searchData).some(
      (v) => String(v || "").trim() !== "",
    );

    if (!hasSearchCriteria) {
      toast.warning("Please enter at least one search criteria!");
      return;
    }

    try {
      setLoading(true);
      setSelectedRows([]);
      toast.info("Searching EMI payment customers...");

      const { data } = await axios.get(`${BASE_URL}/api/emiListing/search`, {
        params: buildQueryParams(),
        headers: commonHeaders,
      });

      if (data?.success) {
        setEmiCustomerData(Array.isArray(data.data) ? data.data : []);
        toast.success(`Search completed! (${(data.data || []).length} rows)`);
      } else {
        toast.error(data?.message || "Search failed");
        setEmiCustomerData([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Search failed");
      setEmiCustomerData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate("/Sales/EMIPaymentEntry");
  };

  const handleViewSelected = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one row to view.");
      return;
    }

    navigate("/Sales/EMIPaymentEntry", {
      state: { selectedRows },
    });
  };
  // -------------------------
  // Update Selected Row
  // -------------------------
  const handleUpdate = (row) => {
    const id = getRowId(row);

    if (!id) {
      toast.warning("Invalid record selected.");
      return;
    }

    navigate(`/Sales/EMIPaymentEntry?id=${id}`);
  };

  // -------------------------
  // Delete Selected Rows
  // -------------------------
  const handleDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one row to delete.");
      return;
    }

    const ok = window.confirm(
      `Delete ${selectedRows.length} selected record(s)?`,
    );

    if (!ok) return;

    try {
      setLoadingDelete(true);

      const ids = selectedRows.map((row) => getRowId(row)).filter((id) => id);

      if (ids.length === 0) {
        toast.warning("Invalid record selected.");
        return;
      }

      await axios.delete(`${BASE_URL}/api/emiListing/deleteSelected`, {
        headers: commonHeaders,
        data: { ids },
      });

      toast.success("Deleted successfully!");

      setEmiCustomerData((prev) =>
        prev.filter((row) => !ids.includes(getRowId(row))),
      );

      setSelectedRows([]);
    } catch (err) {
      console.error(err);

      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setLoadingDelete(false);
    }
  };
  // -------------------------
  // Initial Load
  // -------------------------
  useEffect(() => {
    fetchAssetSubTypes();
  }, [commonHeaders]);

  // -------------------------
  // Summary blocks
  // -------------------------
  const totalAssetValue = useMemo(
    () =>
      emiCustomerData.reduce((sum, r) => sum + Number(r.assetValue || 0), 0),
    [emiCustomerData],
  );

  const totalMonthlyEmi = useMemo(
    () =>
      emiCustomerData.reduce(
        (sum, r) => sum + Number(r.emiAmountMonth || 0),
        0,
      ),
    [emiCustomerData],
  );

  const pendingCount = useMemo(() => {
    return emiCustomerData.filter(
      (r) =>
        String(r.paymentStatus || r.currentMonthPaymentStatus || "")
          .toLowerCase()
          .trim() === "pending",
    ).length;
  }, [emiCustomerData]);

  const allSelected =
    emiCustomerData.length > 0 &&
    selectedRows.length === emiCustomerData.length;

  const clearFilters = () => {
    setSearchData({
      enquiryNumber: "",
      customerName: "",
      customerId: "",
      assetSubType: "",
      doB: "",
      paymentStatus: "",
    });

    setEmiCustomerData([]);
    setSelectedRows([]);

    toast.info("Filters cleared");
  };
  return (
    <SalesLayoutWrapper>
      <div className="w-full">
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">EMI PAYMENT CUSTOMER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              EMI Payment Customer
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Add New */}
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              {/* Show / Hide Filters */}
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              {/* Search Criteria Header */}
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  {/* Clear */}
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  {/* Search */}
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              {/* Filter Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Enquiry Number */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaHashtag className="inline mr-2" />
                    Enquiry Number
                  </label>

                  <input
                    type="text"
                    value={searchData.enquiryNumber}
                    onChange={(e) =>
                      handleInputChange("enquiryNumber", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                {/* Customer Name */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaUser className="inline mr-2" />
                    Customer Name
                  </label>

                  <input
                    type="text"
                    value={searchData.customerName}
                    onChange={(e) =>
                      handleInputChange("customerName", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                {/* Customer ID */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaHashtag className="inline mr-2" />
                    Customer ID
                  </label>

                  <input
                    type="text"
                    value={searchData.customerId}
                    onChange={(e) =>
                      handleInputChange("customerId", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                {/* Asset Sub Type */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Asset Sub Type
                  </label>

                  <select
                    value={searchData.assetSubType}
                    onChange={(e) =>
                      handleInputChange("assetSubType", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>

                    {assetSubTypes.map((t) => (
                      <option
                        key={t.fld_assetsubtypeid}
                        value={t.fld_assetsubtypeid}
                      >
                        {t.fld_assetsubtypename}
                      </option>
                    ))}
                  </select>
                </div>

                {/* DoB */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <FaCalendarAlt className="inline mr-2" />
                    DoB
                  </label>

                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                {/* Payment Status */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Payment Status
                  </label>

                  <select
                    value={searchData.paymentStatus}
                    onChange={(e) =>
                      handleInputChange("paymentStatus", e.target.value)
                    }
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>

                    {paymentStatuses.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== TABLE ==================== */}
          <div className="w-full min-w-0 overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  {/* Sl No */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Sl No
                  </th>

                  {/* Customer ID */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Customer ID
                  </th>

                  {/* Customer Name */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Customer Name
                  </th>

                  {/* Plot Number */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Plot Number
                  </th>

                  {/* Asset Code */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Asset Code
                  </th>

                  {/* Asset Sub Type */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Asset Sub Type
                  </th>

                  {/* Asset Type / Model */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Asset Type / Model
                  </th>

                  {/* Asset Value */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Asset Value
                  </th>

                  {/* Interest Rate */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Interest Rate
                  </th>

                  {/* OverDue Interest */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    OverDue Interest
                  </th>

                  {/* Tenure Period */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Tenure Period
                  </th>

                  {/* EMI Amount */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    EMI Amount/month
                  </th>

                  {/* EMI Activate Date */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    EMI ACTIVATE DATE
                  </th>

                  {/* Due Remaining */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Due Remaining
                  </th>

                  {/* Payment Status */}
                  <th className="px-6 py-4 text-left text-sm font-semibold">
                    Payment Status
                  </th>

                  {/* View */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">
                    View
                  </th>

                  {/* Select All - LAST */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={emiCustomerData.length === 0}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody
                className={`${
                  isDark ? "divide-gray-700" : "divide-gray-200"
                } divide-y`}
              >
                {emiCustomerData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={18}
                      className={`px-6 py-10 text-center ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No Records Found!
                    </td>
                  </tr>
                ) : (
                  emiCustomerData.map((row, index) => (
                    <tr
                      key={rowKey(row)}
                      className={`${
                        index % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                      } ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                      } transition-colors`}
                    >
                      {/* Sl No */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.slNo ?? index + 1}
                      </td>

                      {/* Customer ID */}
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {row.customerId}
                      </td>

                      {/* Customer Name */}
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {row.customerName}
                      </td>

                      {/* Plot Number */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.plotNumber}
                      </td>

                      {/* Asset Code */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.assetCode}
                      </td>

                      {/* Asset Sub Type */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.assetSubTypeName}
                      </td>

                      {/* Asset Type / Model */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.assetTypeModel}
                      </td>

                      {/* Asset Value */}
                      <td
                        className={`px-6 py-4 text-sm font-semibold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {formatAmount(row.assetValue)}
                      </td>

                      {/* Interest Rate */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {Number(row.interestRate || 0)}%
                      </td>

                      {/* OverDue Interest */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {Number(row.overdueInterest || 0)}%
                      </td>

                      {/* Tenure */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.tenurePeriod}
                      </td>

                      {/* EMI Amount */}
                      <td
                        className={`px-6 py-4 text-sm font-semibold ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {formatAmount(row.emiAmountMonth)}
                      </td>

                      {/* EMI Activate Date */}
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {row.emiActivateDate}
                      </td>

                      {/* Due Remaining */}
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDueRemainingColor(
                            row.dueRemaining,
                          )}`}
                        >
                          {row.dueRemaining}
                        </span>
                      </td>

                      {/* Payment Status */}
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(
                            row.paymentStatus || row.currentMonthPaymentStatus,
                          )}`}
                        >
                          {row.paymentStatus || row.currentMonthPaymentStatus}
                        </span>
                      </td>

                      {/* EDIT - SECOND LAST */}
                      <td className="px-6 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleUpdate(row)}
                         className={`inline-flex items-center justify-center p-2 rounded-lg transition ${
                            isDark
                              ? "text-blue-400 hover:bg-gray-700"
                              : "text-blue-600 hover:bg-blue-50"
                          }`}
                          title="View"
                        >
                         <FaEye />
                        </button>
                      </td>

                      {/* SELECT - LAST */}
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isRowSelected(row)}
                          onChange={(e) =>
                            handleRowSelect(row, e.target.checked)
                          }
                          className="w-4 h-4 cursor-pointer"
                          title="Select row"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ==================== SUMMARY ==================== */}
          <div
            className={`mt-6 p-4 rounded-lg border ${
              isDark
                ? "bg-gray-800/30 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4
                  className={`font-semibold ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Search Results
                </h4>

                <p
                  className={`text-sm mt-1 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Showing {emiCustomerData.length} EMI payment customers
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div
                  className={`p-3 rounded-lg ${
                    isDark ? "bg-green-900/30" : "bg-green-50"
                  }`}
                >
                  <div className="text-lg font-bold text-green-600">
                    {formatAmount(totalAssetValue)}
                  </div>

                  <div
                    className={`text-xs ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Total Asset Value
                  </div>
                </div>

                <div
                  className={`p-3 rounded-lg ${
                    isDark ? "bg-blue-900/30" : "bg-blue-50"
                  }`}
                >
                  <div className="text-lg font-bold text-blue-600">
                    {formatAmount(totalMonthlyEmi)}
                  </div>

                  <div
                    className={`text-xs ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Monthly EMI Total
                  </div>
                </div>

                <div
                  className={`p-3 rounded-lg ${
                    isDark ? "bg-yellow-900/30" : "bg-yellow-50"
                  }`}
                >
                  <div className="text-lg font-bold text-yellow-600">
                    {pendingCount}
                  </div>

                  <div
                    className={`text-xs ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Pending
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default EMIPaymentCustomer;
